import { CheckCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import type { SupplierSupplyDetail, SupplierSupplyOrder, SupplyItemRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount, formatDisplayTime, formatQty } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Card, Descriptions, Drawer, Popconfirm, Segmented, Space, Spin, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { hq } from '../session';
import { ellipsisCell, sumWidth } from '@hq/ui-kit';

const PAGE_SIZE = 20;

/** `wl_supply_order.supply_status`（后端建单写 NEW，出库确认写 SHIPPED）。 */
const STATUS_META: Record<string, { text: string; color: string }> = {
  NEW: { text: '待接单', color: 'gold' },
  ACCEPTED: { text: '已接单', color: 'blue' },
  PICKING: { text: '拣货中', color: 'blue' },
  PACKED: { text: '已打包', color: 'cyan' },
  SHIPPED: { text: '已出库', color: 'green' },
  DELIVERING: { text: '配送中', color: 'cyan' },
  SIGNED: { text: '已签收', color: 'green' },
  CANCELED: { text: '已取消', color: 'default' }
};

/** `wl_supply_order.trigger_type`：判源为什么走到外仓。 */
const TRIGGER_META: Record<string, string> = {
  STOCK_OUT: '院内缺货',
  FORCE_OUTER: '指定外仓',
  EMERGENCY: '应急供货',
  SUPPLIER_SYNC: '供应商同步'
};

type StatusFilter = 'ALL' | 'NEW' | 'ACCEPTED' | 'SHIPPED' | 'CANCELED';

const FILTERS: { label: string; value: StatusFilter }[] = [
  { label: '全部', value: 'ALL' },
  { label: '待接单', value: 'NEW' },
  { label: '已接单', value: 'ACCEPTED' },
  { label: '已出库', value: 'SHIPPED' },
  { label: '已取消', value: 'CANCELED' }
];

/**
 * 可供库存（供应商端）：院内判源判定「走外仓代供」后生成的供货指令（GY 单）。
 *
 * 可见范围完全由后端按令牌绑定的供应商主体裁剪（`wl_supply_order.supplier_id`），
 * 前端不传 tenant/org，也不做二次过滤——供应商只能看到派给自己这家的指令。
 *
 * 出库确认（`POST /api/wl/supply/{id}/out`）在事务内完成「院内入即出」配对双单：
 * 库存净增为 0，但会留下 RK/CK 两条流水，供成本转移与结算追溯。
 */
export default function ShopStock() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<StatusFilter>('ALL');
  const [currentId, setCurrentId] = useState<string | null>(null);

  const query = useInfiniteQuery({
    queryKey: ['supplier', 'supply', 'list', status],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.supply.list<SupplierSupplyOrder>({
        lastId: pageParam,
        size: PAGE_SIZE,
        supplyStatus: status === 'ALL' ? undefined : status
      }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const detail = useQuery({
    queryKey: ['supplier', 'supply', 'detail', currentId],
    placeholderData: keepPreviousData,
    queryFn: () => hq.supply.detail<SupplierSupplyDetail>(currentId ?? ''),
    enabled: Boolean(currentId)
  });

  const out = useMutation({
    mutationFn: (id: string) => hq.supply.out(id),
    onSuccess: async (result) => {
      message.success(`已确认出库（${result.supplyBizNo}），院内入库单 ${result.inboundBizNo}`);
      await queryClient.invalidateQueries({ queryKey: ['supplier', 'supply'] });
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '出库确认失败，请稍后重试');
    }
  });

  const rows = (query.data?.pages.flatMap((page) => page.items) ?? []) as SupplierSupplyOrder[];

  const canShip = (row: { supplyStatus: string }) =>
    ['NEW', 'ACCEPTED', 'PICKING', 'PACKED'].includes(row.supplyStatus);

  const columns: ColumnsType<SupplierSupplyOrder> = [
    {
      title: '供货单号',
      dataIndex: 'bizNo',
      width: 210,
      render: (value: string, row) => <Typography.Link onClick={() => setCurrentId(row.id)}>{value}</Typography.Link>
    },
    { title: '需求科室', dataIndex: 'demandDeptName', width: 150, render: (value: string | null) => value || '-' },
    { title: '承接仓', dataIndex: 'outerWarehouseName', width: 160, render: (value: string | null) => value || '-' },
    {
      title: '触发原因',
      dataIndex: 'triggerType',
      width: 110,
      render: (value: string) => TRIGGER_META[value] ?? value
    },
    {
      title: '状态',
      dataIndex: 'supplyStatus',
      width: 110,
      render: (value: string) => {
        const meta = STATUS_META[value] ?? { text: value, color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    { title: '数量', dataIndex: 'totalQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
    {
      title: '应急',
      dataIndex: 'isEmergency',
      width: 80,
      render: (value: boolean) => (value ? <Tag color="red">应急</Tag> : '-')
    },
    {
      title: '应到时间',
      dataIndex: 'slaDeadline',
      width: 170,
      render: (value: string | null, row) =>
        value ? (
          <Space size={4}>
            <span>{formatDisplayTime(value)}</span>
            {row.isOvertime ? <Tag color="red">超时</Tag> : null}
          </Space>
        ) : (
          '-'
        )
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, row) =>
        canShip(row) ? (
          <Popconfirm
            title="确认出库？"
            description="确认后院内会自动生成「入即出」单据，无法撤回。"
            okText="确认出库"
            cancelText="取消"
            onConfirm={() => out.mutate(row.id)}
          >
            <Button type="link" size="small" icon={<CheckCircleOutlined />} loading={out.isPending}>
              出库确认
            </Button>
          </Popconfirm>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        )
    }
  ];

  const itemColumns: ColumnsType<SupplyItemRow> = [
    { title: '物资编码', dataIndex: 'materialCode', width: 150, render: (value: string | null) => value || '-' },
    { title: '品名', dataIndex: 'materialName', ellipsis: { showTitle: false }, render: ellipsisCell('materialName') },
    { title: '规格', dataIndex: 'spec', width: 140, render: (value: string | null) => value || '-' },
    { title: '单位', dataIndex: 'unitName', width: 80, render: (value: string | null) => value || '-' },
    { title: '数量', dataIndex: 'planQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
    { title: '已出', dataIndex: 'outQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
    { title: '批号', dataIndex: 'batchNo', width: 120, render: (value: string | null) => value || '-' },
    {
      title: '效期',
      dataIndex: 'expiryDate',
      width: 120,
      render: (value: string | null) => (value ? String(value).slice(0, 10) : '-')
    },
    { title: '单价', dataIndex: 'price', width: 110, align: 'right', render: (value: number) => formatAmount(value) },
    { title: '金额', dataIndex: 'amount', width: 120, align: 'right', render: (value: number) => formatAmount(value) }
  ];

  const current = detail.data?.order;

  return (
    <Card
      className="page-card"
      title="可供库存"
      extra={
        <Button icon={<ReloadOutlined />} onClick={() => query.refetch()} loading={query.isFetching}>
          刷新
        </Button>
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="这里只显示派给本企业的外仓供货指令（GY 单）。"
        description="院内判源判定走外仓代供后会自动通知；点「出库确认」即完成发货，院内同一事务内完成入库与科室出库（入即出），无需在医院端再操作。"
      />

      <Segmented<StatusFilter>
        value={status}
        options={FILTERS}
        onChange={(value) => setStatus(value)}
        style={{ marginBottom: 12 }}
      />

      {query.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="供货指令加载失败"
          description={query.error instanceof ApiError ? query.error.friendlyMessage : String(query.error)}
        />
      ) : null}

      <Table<SupplierSupplyOrder>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={query.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: query.isError ? '加载失败，请重试' : '暂无供货指令' }}
        onRow={(row) => ({ onDoubleClick: () => setCurrentId(row.id) })}
      />

      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
        {query.hasNextPage ? (
          <Button onClick={() => query.fetchNextPage()} loading={query.isFetchingNextPage}>
            加载更多
          </Button>
        ) : null}
      </Space>

      <Drawer
        title={current?.bizNo ?? '供货指令详情'}
        open={currentId !== null}
        onClose={() => setCurrentId(null)}
        width={900}
      >
        {detail.isLoading ? (
          <Spin />
        ) : detail.isError ? (
          <Alert
            type="error"
            showIcon
            message="详情加载失败"
            description={detail.error instanceof ApiError ? detail.error.friendlyMessage : String(detail.error)}
          />
        ) : current ? (
          <>
            <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="状态">
                {STATUS_META[current.supplyStatus]?.text ?? current.supplyStatus}
              </Descriptions.Item>
              <Descriptions.Item label="触发原因">{TRIGGER_META[current.triggerType] ?? current.triggerType}</Descriptions.Item>
              <Descriptions.Item label="需求科室">{current.demandDeptName || '-'}</Descriptions.Item>
              <Descriptions.Item label="承接仓">{current.outerWarehouseName || '-'}</Descriptions.Item>
              <Descriptions.Item label="数量合计">{formatQty(current.totalQty)}</Descriptions.Item>
              <Descriptions.Item label="应急">{current.isEmergency ? '是' : '否'}</Descriptions.Item>
              <Descriptions.Item label="通知时间">{formatDisplayTime(current.notifyAt)}</Descriptions.Item>
              <Descriptions.Item label="应到时间">
                {current.slaDeadline ? (
                  <Space size={4}>
                    <span>{formatDisplayTime(current.slaDeadline)}</span>
                    {current.isOvertime ? <Tag color="red">超时</Tag> : null}
                  </Space>
                ) : (
                  '-'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="出库时间" span={2}>
                {formatDisplayTime(current.outAt)}
              </Descriptions.Item>
            </Descriptions>

            <Space style={{ marginBottom: 12 }}>
              {canShip(current) ? (
                <Popconfirm
                  title="确认出库？"
                  description="确认后院内会自动生成「入即出」单据，无法撤回。"
                  okText="确认出库"
                  cancelText="取消"
                  onConfirm={() => out.mutate(current.id)}
                >
                  <Button type="primary" loading={out.isPending} icon={<CheckCircleOutlined />}>
                    出库确认
                  </Button>
                </Popconfirm>
              ) : (
                <Typography.Text type="secondary">该指令当前状态不可再确认出库。</Typography.Text>
              )}
            </Space>

            <Table<SupplyItemRow>
              rowKey="id"
              size="small"
              columns={itemColumns}
              dataSource={detail.data?.items ?? []}
              pagination={false}
              scroll={{ x: sumWidth(itemColumns) }}
              locale={{ emptyText: '暂无明细' }}
            />
          </>
        ) : null}
      </Drawer>
    </Card>
  );
}
